import * as React from 'react';
import { CommandBarButton, PrimaryButton } from '@fluentui/react';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../system';
import MessageCourier from '../../../lib/MessageCourier';
import { User } from '../../../system/User';
import { useTranslation } from 'react-i18next';
import useAsyncDataCourier from '../../../common/custom-hooks/useAsyncDataCourier';
import { DateCustom } from '../../../lib/DateCustom';
import { DatePicker, IIconProps, IStackStyles } from '@fluentui/react';
import { getLocalizedStrings } from '../../../lib/LocalizedStringsForDatePicker';
import { ALL_SECTORS, OrganizationChartDropdown } from '../../Common/OrganizationChartDropdown';

interface IPanelProjectsSelectorCollaborator {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  onChangeOrganizationNodeId: (id: string) => void;
  onChangePeriod: (date: DateTime) => void;
  user: User;
  onClickSearch: () => void;
  useAllSector?: boolean;
}

export const PanelProjectsSelectorCollaborator = (props: IPanelProjectsSelectorCollaborator) => {
  const { t } = useTranslation();

  const [period, setPeriod] = React.useState<DateTime>(DateTime.now());

  const nodeOfLoggedCol = useAsyncDataCourier(
    () => props.system.getOrganizationChartSystem().getNodeOf(props.user.getCollaborator()),
    [props.system, props.user],
    props.courier,
  );

  const nodes = useAsyncDataCourier(
    () => props.system.getOrganizationChartSystem().getAvailableNodesOf(props.user),
    [props.system, props.user],
    props.courier,
  );

  React.useEffect(() => {
    const setDefaultValues = async () => {
      const today = DateTime.now();

      setPeriod(today);
      props.onChangePeriod(today);

      const nodeOfColLogged = await props.system.getOrganizationChartSystem().getNodeOf(props.user.getCollaborator());

      props.onChangeOrganizationNodeId(nodeOfColLogged.getId());
    };

    setDefaultValues();
  }, []);

  const onChangeDate = (date: Date | null | undefined) => {
    if (!date) return;

    const selectedDate = DateTime.fromJSDate(date);
    setPeriod(selectedDate);
    props.onChangePeriod(selectedDate);
  };

  const onClearFilters = async () => {
    setPeriod(DateTime.now());
    props.onChangePeriod(DateTime.now());

    const nodeOfColLogged = await props.system.getOrganizationChartSystem().getNodeOf(props.user.getCollaborator());

    props.onChangeOrganizationNodeId(nodeOfColLogged.getId());
  };

  const FilterClearIcon: IIconProps = { iconName: 'ClearFilter' };

  const stackStyles: Partial<IStackStyles> = {
    root: {
      height: 32,
      fontSize: 12,
      paddingLeft: 0,
      marginRight: '10px',
    },
  };

  return (
    <div>
      {nodes && nodeOfLoggedCol && (
        <div className="ms-Grid-row mt-12">
          <div className="ms-Grid-col ms-sm12" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <CommandBarButton
              onClick={onClearFilters}
              className="float-right-media bg-transparent"
              allowDisabledFocus
              styles={stackStyles}
              iconProps={FilterClearIcon}
              text={t('Limpiar')}
            />
          </div>

          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
            <DatePicker
              strings={getLocalizedStrings()}
              label={t('Periodo')}
              formatDate={DateCustom.formatDateMMYYYY}
              value={period.toJSDate()}
              onSelectDate={onChangeDate}
              calendarProps={{
                isDayPickerVisible: false,
              }}
            />
          </div>

          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
            <OrganizationChartDropdown
              options={nodes}
              label={t('Sector')}
              onChange={(node) => props.onChangeOrganizationNodeId(node.getId())}
              defaultSelectedKey={nodeOfLoggedCol.getId()}
              useAllSector={props.useAllSector && props.user.hasPowerUserPrivileges()}
              onSelectAll={() => props.onChangeOrganizationNodeId(ALL_SECTORS)}
            />
          </div>

          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg3">
            <PrimaryButton
              iconProps={{ iconName: 'Search' }}
              className="mt-for-btn-search ml-2 panel-collaborators-search-btn"
              onClick={props.onClickSearch}
              text={t('Buscar')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
